import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import {
  FileText, Plus, Download, Eye, Trash2, Clock, CheckCircle,
  Settings, ChevronRight, Loader2, Calendar, FileJson, FileSpreadsheet,
  Globe, AlertTriangle, Shield, Server, Activity, BarChart3, Filter,
  Save, RefreshCw, Edit2, Copy, Play, Upload, Image, Palette, Type,
  GripVertical, X, ChevronUp, ChevronDown, Building2, Mail, Phone, Link
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

export default function ReportBuilder() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  const fileInputRef = useRef(null);
  
  // Report Templates
  const [templates, setTemplates] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  
  // Create/Edit Template Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    format: 'pdf',
    sections: [],
    include_charts: true,
    include_executive_summary: true,
    include_technical_details: true,
    schedule: 'manual',
    recipients: '',
    // Company Branding Options
    branding: {
      company_name: '',
      logo_url: '',
      primary_color: '#6366f1',
      secondary_color: '#8b5cf6',
      header_text: '',
      footer_text: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      show_logo: true,
      show_header: true,
      show_footer: true
    },
    // Custom Content Sections
    custom_sections: []
  });
  
  // Generate Report Dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Custom Section Dialog
  const [showCustomSectionDialog, setShowCustomSectionDialog] = useState(false);
  const [customSection, setCustomSection] = useState({
    title: '',
    content: '',
    position: 'end'
  });
  
  // Available sections for reports
  const availableSections = [
    { id: 'executive_summary', name: 'Executive Summary', icon: FileText, description: 'High-level overview for leadership' },
    { id: 'vulnerability_overview', name: 'Vulnerability Overview', icon: AlertTriangle, description: 'Summary of discovered vulnerabilities' },
    { id: 'asset_inventory', name: 'Asset Inventory', icon: Server, description: 'Complete asset listing and status' },
    { id: 'threat_landscape', name: 'Threat Landscape', icon: Shield, description: 'Current threat intelligence findings' },
    { id: 'compliance_status', name: 'Compliance Status', icon: CheckCircle, description: 'Regulatory compliance overview' },
    { id: 'remediation_progress', name: 'Remediation Progress', icon: Activity, description: 'Remediation tasks and status' },
    { id: 'risk_metrics', name: 'Risk Metrics', icon: BarChart3, description: 'Risk scores and trends' },
    { id: 'easm_findings', name: 'External Attack Surface', icon: Globe, description: 'EASM scan results and exposures' },
    { id: 'bas_results', name: 'BAS Simulation Results', icon: Shield, description: 'Breach attack simulation findings' },
    { id: 'pentest_findings', name: 'Penetration Test Results', icon: AlertTriangle, description: 'AI pentesting findings' },
    { id: 'automation_activity', name: 'Automation Activity', icon: Settings, description: 'Automated actions and triggers' },
    { id: 'technical_appendix', name: 'Technical Appendix', icon: FileJson, description: 'Detailed technical data and IOCs' }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [templatesRes, reportsRes, scheduledRes] = await Promise.all([
        api.get('/reports/templates').catch(() => ({ data: { templates: [] } })),
        api.get('/reports/generated?limit=20').catch(() => ({ data: { reports: [] } })),
        api.get('/reports/scheduled').catch(() => ({ data: { scheduled: [] } }))
      ]);
      
      setTemplates(templatesRes.data.templates || templatesRes.data || []);
      setGeneratedReports(reportsRes.data.reports || reportsRes.data || []);
      setScheduledReports(scheduledRes.data.scheduled || scheduledRes.data || []);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetTemplateForm = () => {
    setNewTemplate({
      name: '',
      description: '',
      format: 'pdf',
      sections: [],
      include_charts: true,
      include_executive_summary: true,
      include_technical_details: true,
      schedule: 'manual',
      recipients: '',
      branding: {
        company_name: '',
        logo_url: '',
        primary_color: '#6366f1',
        secondary_color: '#8b5cf6',
        header_text: '',
        footer_text: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        show_logo: true,
        show_header: true,
        show_footer: true
      },
      custom_sections: []
    });
    setEditingTemplate(null);
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || newTemplate.sections.length === 0) {
      toast.error('Please provide a name and select at least one section');
      return;
    }

    try {
      if (editingTemplate) {
        // Update existing template
        const response = await api.put(`/reports/templates/${editingTemplate.id}`, newTemplate);
        toast.success('Report template updated successfully');
        setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data : t));
      } else {
        // Create new template
        const response = await api.post('/reports/templates', newTemplate);
        toast.success('Report template created successfully');
        setTemplates([...templates, response.data]);
      }
      setShowCreateDialog(false);
      resetTemplateForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      ...template,
      branding: template.branding || {
        company_name: '',
        logo_url: '',
        primary_color: '#6366f1',
        secondary_color: '#8b5cf6',
        header_text: '',
        footer_text: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        show_logo: true,
        show_header: true,
        show_footer: true
      },
      custom_sections: template.custom_sections || []
    });
    setShowCreateDialog(true);
  };

  const handleGenerateReport = async () => {
    if (!selectedTemplate) return;
    
    setGeneratingReport(true);
    try {
      const response = await api.post('/reports/generate', {
        template_id: selectedTemplate.id,
        date_range: dateRange.start && dateRange.end ? dateRange : null
      });
      
      toast.success('Report generated successfully');
      setGeneratedReports([response.data, ...generatedReports]);
      setShowGenerateDialog(false);
      setActiveTab('generated'); // Switch to generated tab
      
      // Trigger download if URL is provided
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadReport = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.name || 'report'}.${report.format || 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (report.download_url) {
        window.open(report.download_url, '_blank');
      } else {
        toast.error('Failed to download report');
      }
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/reports/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const addCustomSection = () => {
    if (!customSection.title) {
      toast.error('Please provide a section title');
      return;
    }
    
    const newCustomSection = {
      id: `custom_${Date.now()}`,
      ...customSection
    };
    
    setNewTemplate({
      ...newTemplate,
      custom_sections: [...newTemplate.custom_sections, newCustomSection]
    });
    
    setCustomSection({ title: '', content: '', position: 'end' });
    setShowCustomSectionDialog(false);
    toast.success('Custom section added');
  };

  const removeCustomSection = (sectionId) => {
    setNewTemplate({
      ...newTemplate,
      custom_sections: newTemplate.custom_sections.filter(s => s.id !== sectionId)
    });
  };

  const moveSection = (index, direction) => {
    const sections = [...newTemplate.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    setNewTemplate({ ...newTemplate, sections });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewTemplate({
          ...newTemplate,
          branding: { ...newTemplate.branding, logo_url: event.target.result }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'pdf': return FileText;
      case 'excel': return FileSpreadsheet;
      case 'json': return FileJson;
      case 'html': return Globe;
      default: return FileText;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
          <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Loading Report Builder...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 sm:p-6 space-y-6 ${isDarkMode ? 'bg-black text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Report Builder
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            Create custom reports with company branding
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              resetTemplateForm();
              setShowCreateDialog(true);
            }}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Templates', value: templates.length, color: 'violet', icon: FileText },
          { label: 'Generated', value: generatedReports.length, color: 'emerald', icon: CheckCircle },
          { label: 'Scheduled', value: scheduledReports.length, color: 'blue', icon: Calendar },
          { label: 'Available Sections', value: availableSections.length, color: 'orange', icon: Settings }
        ].map((stat) => (
          <div key={stat.label} className={`rounded-xl p-4 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 text-${stat.color}-500`} />
              <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{stat.label}</span>
            </div>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="generated" data-testid="generated-tab">
            <CheckCircle className="w-4 h-4 mr-2" />
            Generate Reports
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="scheduled-tab">
            <Calendar className="w-4 h-4 mr-2" />
            Scheduled
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          {templates.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
              <FileText className={`w-12 h-12 mx-auto mb-4 opacity-50 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No report templates yet</p>
              <p className={`text-sm mt-1 mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Create your first template with company branding
              </p>
              <Button onClick={() => { resetTemplateForm(); setShowCreateDialog(true); }} className="bg-violet-600 hover:bg-violet-500">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => {
                const FormatIcon = getFormatIcon(template.format);
                return (
                  <div key={template.id} className={`rounded-xl p-5 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {template.branding?.logo_url ? (
                          <img src={template.branding.logo_url} alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
                        ) : (
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                            <FormatIcon className={`w-5 h-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                          </div>
                        )}
                        <div>
                          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{template.name}</h3>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {template.sections?.length || 0} sections • {template.format?.toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <Badge className={`text-xs ${
                        template.schedule === 'manual' ? 'bg-zinc-500/20 text-zinc-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {template.schedule === 'manual' ? 'Manual' : template.schedule}
                      </Badge>
                    </div>
                    
                    {template.branding?.company_name && (
                      <p className={`text-xs mb-2 flex items-center gap-1 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                        <Building2 className="w-3 h-3" />
                        {template.branding.company_name}
                      </p>
                    )}
                    
                    <p className={`text-sm mb-4 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {template.description || 'No description provided'}
                    </p>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowGenerateDialog(true);
                        }}
                        className="flex-1 bg-violet-600 hover:bg-violet-500"
                        data-testid={`generate-${template.id}`}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Generate
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEditTemplate(template)}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Generated Reports Tab */}
        <TabsContent value="generated" className="space-y-4">
          {/* Template Selection for Generation */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Generate New Report</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowGenerateDialog(true);
                  }}
                  className={`p-4 rounded-lg text-left transition-all hover:scale-[1.02] ${
                    isDarkMode 
                      ? 'bg-zinc-800 hover:bg-zinc-700 border border-white/5' 
                      : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-4 h-4 text-violet-500" />
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{template.name}</span>
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {template.sections?.length || 0} sections • {template.format?.toUpperCase()}
                  </p>
                </button>
              ))}
              {templates.length === 0 && (
                <p className={`col-span-full text-center py-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Create a template first to generate reports
                </p>
              )}
            </div>
          </div>

          {/* Generated Reports List */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Recent Reports</h3>
            {generatedReports.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No reports generated yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {generatedReports.map((report) => {
                  const FormatIcon = getFormatIcon(report.format);
                  return (
                    <div key={report.id} className={`rounded-xl p-4 flex items-center justify-between ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                          <FormatIcon className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        </div>
                        <div>
                          <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{report.name || report.template_name || 'Report'}</h3>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                            Generated {new Date(report.created_at).toLocaleString()} • {report.format?.toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${
                          report.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                          report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {report.status || 'completed'}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => handleDownloadReport(report)} data-testid={`download-${report.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Scheduled Tab */}
        <TabsContent value="scheduled" className="space-y-4">
          {scheduledReports.length === 0 ? (
            <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200'}`}>
              <Calendar className={`w-12 h-12 mx-auto mb-4 opacity-50 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No scheduled reports</p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Create a template with a schedule to automate report generation
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledReports.map((scheduled) => (
                <div key={scheduled.id} className={`rounded-xl p-4 flex items-center justify-between ${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{scheduled.template_name}</h3>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {scheduled.schedule} • Next run: {new Date(scheduled.next_run).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={`text-xs ${scheduled.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                    {scheduled.enabled ? 'Active' : 'Paused'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetTemplateForm(); }}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500" />
              {editingTemplate ? 'Edit Report Template' : 'Create Report Template'}
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              Configure your report template with branding and content options
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className={isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}>
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="branding">Company Branding</TabsTrigger>
              <TabsTrigger value="sections">Sections</TabsTrigger>
              <TabsTrigger value="custom">Custom Content</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Template Name *</Label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                    placeholder="e.g., Monthly Security Report"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Export Format</Label>
                  <Select value={newTemplate.format} onValueChange={(v) => setNewTemplate({...newTemplate, format: v})}>
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="json">JSON Data</SelectItem>
                      <SelectItem value="html">HTML Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Description</Label>
                <Textarea
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                  placeholder="Brief description of the report"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  rows={3}
                />
              </div>

              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Schedule</Label>
                <Select value={newTemplate.schedule} onValueChange={(v) => setNewTemplate({...newTemplate, schedule: v})}>
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Generation</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.include_charts}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, include_charts: checked})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Include Charts</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.include_executive_summary}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, include_executive_summary: checked})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Executive Summary</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.include_technical_details}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, include_technical_details: checked})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Technical Details</span>
                </label>
              </div>
            </TabsContent>

            {/* Company Branding Tab */}
            <TabsContent value="branding" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Company Name</Label>
                  <Input
                    value={newTemplate.branding.company_name}
                    onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, company_name: e.target.value}})}
                    placeholder="Your Company Name"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Website</Label>
                  <Input
                    value={newTemplate.branding.website}
                    onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, website: e.target.value}})}
                    placeholder="https://yourcompany.com"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  />
                </div>
              </div>

              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Company Logo</Label>
                <div className="mt-1 flex items-center gap-4">
                  {newTemplate.branding.logo_url ? (
                    <div className="relative">
                      <img src={newTemplate.branding.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-contain border" />
                      <button
                        onClick={() => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, logo_url: ''}})}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className={`w-16 h-16 rounded-lg border-2 border-dashed flex items-center justify-center ${isDarkMode ? 'border-white/20 hover:border-white/40' : 'border-slate-300 hover:border-slate-400'}`}
                    >
                      <Upload className="w-6 h-6 opacity-50" />
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="text-sm">
                    <p className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Upload company logo</p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>PNG, JPG up to 2MB</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Primary Color</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={newTemplate.branding.primary_color}
                      onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, primary_color: e.target.value}})}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={newTemplate.branding.primary_color}
                      onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, primary_color: e.target.value}})}
                      className={`flex-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Secondary Color</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={newTemplate.branding.secondary_color}
                      onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, secondary_color: e.target.value}})}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={newTemplate.branding.secondary_color}
                      onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, secondary_color: e.target.value}})}
                      className={`flex-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Contact Email</Label>
                  <Input
                    value={newTemplate.branding.contact_email}
                    onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, contact_email: e.target.value}})}
                    placeholder="security@company.com"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : ''}>Contact Phone</Label>
                  <Input
                    value={newTemplate.branding.contact_phone}
                    onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, contact_phone: e.target.value}})}
                    placeholder="+1 (555) 123-4567"
                    className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                  />
                </div>
              </div>

              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Header Text</Label>
                <Input
                  value={newTemplate.branding.header_text}
                  onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, header_text: e.target.value}})}
                  placeholder="CONFIDENTIAL - Security Assessment Report"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                />
              </div>

              <div>
                <Label className={isDarkMode ? 'text-zinc-300' : ''}>Footer Text</Label>
                <Input
                  value={newTemplate.branding.footer_text}
                  onChange={(e) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, footer_text: e.target.value}})}
                  placeholder="© 2026 Your Company - All Rights Reserved"
                  className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.branding.show_logo}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, show_logo: checked}})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Show Logo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.branding.show_header}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, show_header: checked}})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Show Header</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTemplate.branding.show_footer}
                    onCheckedChange={(checked) => setNewTemplate({...newTemplate, branding: {...newTemplate.branding, show_footer: checked}})}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-300' : ''}`}>Show Footer</span>
                </label>
              </div>
            </TabsContent>

            {/* Sections Tab */}
            <TabsContent value="sections" className="space-y-4 mt-4">
              <div className={`max-h-80 overflow-y-auto p-3 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Select and reorder sections for your report. Drag to reorder.
                </p>
                <div className="space-y-2">
                  {availableSections.map((section, index) => {
                    const isSelected = newTemplate.sections.includes(section.id);
                    const selectedIndex = newTemplate.sections.indexOf(section.id);
                    return (
                      <div 
                        key={section.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected 
                            ? isDarkMode ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-violet-50 border border-violet-200' 
                            : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-100'
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            setNewTemplate({...newTemplate, sections: newTemplate.sections.filter(s => s !== section.id)});
                          } else {
                            setNewTemplate({...newTemplate, sections: [...newTemplate.sections, section.id]});
                          }
                        }}
                      >
                        <Checkbox checked={isSelected} />
                        {isSelected && (
                          <div className="flex flex-col gap-0.5">
                            <button 
                              onClick={(e) => { e.stopPropagation(); moveSection(selectedIndex, 'up'); }}
                              className="p-0.5 hover:bg-white/10 rounded"
                              disabled={selectedIndex === 0}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); moveSection(selectedIndex, 'down'); }}
                              className="p-0.5 hover:bg-white/10 rounded"
                              disabled={selectedIndex === newTemplate.sections.length - 1}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <section.icon className={`w-4 h-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {isSelected && <span className="text-violet-500 mr-2">#{selectedIndex + 1}</span>}
                            {section.name}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{section.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                {newTemplate.sections.length} sections selected
              </p>
            </TabsContent>

            {/* Custom Content Tab */}
            <TabsContent value="custom" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Custom Sections</p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Add your own content sections to the report</p>
                </div>
                <Button size="sm" onClick={() => setShowCustomSectionDialog(true)} variant="outline">
                  <Plus className="w-3 h-3 mr-1" />
                  Add Section
                </Button>
              </div>

              {newTemplate.custom_sections.length === 0 ? (
                <div className={`text-center py-8 rounded-lg border border-dashed ${isDarkMode ? 'border-white/10' : 'border-slate-300'}`}>
                  <Type className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>No custom sections added</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newTemplate.custom_sections.map((section, index) => (
                    <div key={section.id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{section.title}</h4>
                          <p className={`text-xs mt-1 line-clamp-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{section.content || 'No content'}</p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => removeCustomSection(section.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetTemplateForm(); }}>Cancel</Button>
            <Button onClick={handleCreateTemplate} className="bg-violet-600 hover:bg-violet-500">
              <Save className="w-4 h-4 mr-2" />
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Section Dialog */}
      <Dialog open={showCustomSectionDialog} onOpenChange={setShowCustomSectionDialog}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Add Custom Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Section Title *</Label>
              <Input
                value={customSection.title}
                onChange={(e) => setCustomSection({...customSection, title: e.target.value})}
                placeholder="e.g., Recommendations"
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
              />
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Content</Label>
              <Textarea
                value={customSection.content}
                onChange={(e) => setCustomSection({...customSection, content: e.target.value})}
                placeholder="Enter section content..."
                className={`mt-1 ${isDarkMode ? 'bg-zinc-800 border-white/10' : ''}`}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCustomSectionDialog(false)}>Cancel</Button>
            <Button onClick={addCustomSection} className="bg-violet-600 hover:bg-violet-500">Add Section</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-violet-500" />
              Generate Report
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-zinc-400' : ''}>
              {selectedTemplate?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Date Range (Optional)</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                />
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                />
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Leave empty for all-time data
              </p>
            </div>

            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                <strong>Format:</strong> {selectedTemplate?.format?.toUpperCase()}
              </p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                <strong>Sections:</strong> {selectedTemplate?.sections?.length || 0}
              </p>
              {selectedTemplate?.branding?.company_name && (
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  <strong>Branding:</strong> {selectedTemplate.branding.company_name}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleGenerateReport} 
              disabled={generatingReport}
              className="bg-violet-600 hover:bg-violet-500"
            >
              {generatingReport ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Generate & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
